import { ThemeEvents } from '@theme/events';

/**
 * Keeps the product-label overlay in sync with the selected variant.
 *
 * PORTED TO SHE WEARS FAITH 2026-08-27 from BuildMyPOD 2.0.0 (Horizon 4.1.3)
 * onto this Horizon 3.0.0 tree. The upstream version cannot run here:
 *
 *   upstream (Horizon 4.1.3)          this tree (Horizon 3.0.0)
 *   ------------------------------    ---------------------------------------
 *   import { StandardEvents }         import { ThemeEvents }
 *     from '@shopify/events'            from '@theme/events'
 *   StandardEvents.productSelect      ThemeEvents.variantUpdate
 *   await event.promise -> detail     event.detail.data.html  (synchronous)
 *   morph(this, ...)                  replaceChildren(...)
 *
 * `@shopify/events` is NOT in this theme's importmap (snippets/scripts.liquid),
 * so the upstream module would fail to resolve and the element would never
 * register — silently, with the labels still painted by Liquid. The shape
 * below mirrors assets/product-price.js, which is this theme's own proven
 * variant-update consumer.
 *
 * SCOPE OF WORK, read before "fixing" this. Since the discount label was
 * deliberately not ported (see snippets/bmp-product-labels.liquid), every
 * remaining label — allowlisted tags and New — is PRODUCT-level, not
 * variant-level. Switching colour on a card therefore never changes a pill,
 * and this element is a no-op on the common path. It earns its place in one
 * case only: `data.newProduct`, where a swatch resolves to a genuinely
 * different product (combined listings) whose tags and publish date differ.
 *
 * Children are replaced rather than the element itself, so the context and
 * position classes on the wrapper survive. That matters because a card's
 * fetched HTML may be the PRODUCT PAGE, whose element carries
 * `bmp-product-labels--pdp`; swapping the whole element would change a card's
 * inset scale to the product page's.
 */
class BmpProductLabels extends HTMLElement {
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    if (!this.#scope) return;
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#scope = null;
  }

  /**
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const data = event?.detail?.data;
    if (!data) return;

    // Same product-id guard product-price.js:36-40 uses: one section holds many
    // cards, and every card's variant change bubbles to the shared section.
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    // Absent on this tree's section-rendering-product-card.liquid, which does
    // not render the label overlay. Bail rather than blanking the pills.
    const next = data.html?.querySelector('bmp-product-labels');
    if (!next) return;

    if (next.innerHTML === this.innerHTML) return;
    this.replaceChildren(...Array.from(next.cloneNode(true).childNodes));
  };
}

if (!customElements.get('bmp-product-labels')) {
  customElements.define('bmp-product-labels', BmpProductLabels);
}
